import { useEffect, useState } from 'react';
import { NextPage, NextPageContext } from 'next';
import { triggerTracking } from '@zoopla/tracking';
import Router, { useRouter } from 'next/router';
import {
    ApolloClient,
    ApolloError,
    NormalizedCacheObject
} from '@apollo/client';
import { wrapper } from '@zoopla/redux-store';
import { SearchResults } from '@zoopla/listing-search/results';
import { graphQLWrapper } from '@zoopla/apollo';
import { withSSROptimizely } from '@zoopla/optimizely/server';
import { SearchResultsLayout } from '@zoopla/listing-search/layout';
import {
    LISTINGS_QUERY,
    LISTINGS_QUERY_V2,
    Listing
} from '@zoopla/listing-search-queries';
import { useClientSideRoutingLoading } from '@zoopla/listing-search/use-load-clientside';

import {
    SearchProvider,
    Redirect,
    ServerData,
    SearchContextType,
    AnalyticsTaxonomy,
    SeoBlurb,
    generateInitialSearchState
} from '@zoopla/listing-search-context';
import { SearchType } from '@zoopla/listing-search-context';
import { DfpTargeting } from '@zoopla/adverts/dfp';
import { GraphQLError } from 'graphql';
import { Links } from '@zoopla/listing-search-context';
import { dispatchData } from '@zoopla/redux/dispatch-data';
import {
    getServerRenderedData,
    getServerRenderedDataV2
} from './get-server-rendered-data';
import { UserTaxonomy } from '@zoopla/tracking';
import { internalPromoIds } from '@zoopla/listing-search/featured/properties';
import { FormattedListing } from '@zoopla/listing-search-utils';
import { AppContext } from 'next/app';
import { InformationLayout } from '@zoopla/layout/page';

const CLIENT_SIDE_DATA_FETCH =
    process.env['NEXT_PUBLIC_ENABLE_STATIC_LSRP'] === 'true';

interface NextPageContextWithApollo extends NextPageContext {
    apolloClient: ApolloClient<NormalizedCacheObject> | null;
    apolloState: NormalizedCacheObject;
    ctx: NextPageContextApp;
}

type NextPageContextApp = NextPageContextWithApollo & AppContext;

type InternalPromoId = typeof internalPromoIds[number];
type PromoIds = typeof internalPromoIds[number];

type Product =
    | {
          listingId: string;
          featuredType?: string | null;
          title?: string | null;
          price?: string | null;
          alternativeRentFrequencyLabel?: string | null;
          address?: string | null;
          branch?: { logoUrl?: string | null; name?: string | null } | null;
          listingUris?: {
              detail?: string | null;
          } | null;
          features?:
              | ({
                    iconId?: string | null;
                    content?: number | null;
                } | null)[]
              | null;
          image?: {
              src?: string | null;
              responsiveImgList?:
                  | ({ src?: string | null; width?: number | null } | null)[]
                  | null;
          } | null;
      }
    | InternalPromoId;

type Gallery = [caption: string | null, uri: string][];

type CompressedFormattedListing = Omit<
    FormattedListing<Listing>,
    'otherPropertyImages'
> & { gallery?: Gallery };

export type SearchPageProps = {
    searchResults?: ServerData['searchResults'];
    discoverMore?: ServerData['discoverMore'];
    breadcrumbs?: ServerData['breadcrumbs'];
    initialState: SearchContextType;
    filtersTargeting?: DfpTargeting;
    analyticsTaxonomy?: AnalyticsTaxonomy;
    regularListingsFormatted?: CompressedFormattedListing[];
    extendedListingsFormatted?: CompressedFormattedListing[];
    featuredProperties?: (Product | PromoIds)[];
    transactionType?: SeoBlurb['transactionType'];
    canonicalPath?: string;
    error?: ApolloError;
    links?: Links;
    useNewQuery?: boolean;
    userTaxonomy?: UserTaxonomy;
};

type SearchPagePropsWithApollo = SearchPageProps & {
    apollo: ApolloClient<NormalizedCacheObject> | null;
};

const removeEverythingAfterHash = (path?: string) => {
    if (!path) {
        return '';
    }

    return path.replace(/#.*/, '');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleRedirects = (redirects: Redirect[], res: any) => {
    const { redirectUri, code } = redirects[0];

    // always a server redirect as we don't route to routes that need
    // redirects internally i.e. on the browser
    if (res) {
        res.writeHead(code, { Location: redirectUri });
        res.end();
    }
};

type SearchPageFactoryProps = {
    type: SearchType;
};

const isGeoGraphqlError = (err: GraphQLError): boolean => {
    const errors = err?.extensions?.['response']?.body?.errors;

    if (!Array.isArray(errors)) {
        return false;
    }

    const { validation_errors = [] } = errors[0];

    if (!Array.isArray(validation_errors)) {
        return false;
    }

    const { field } = validation_errors[0] || {};

    return field === 'geo_identifier';
};

const getGeoError = (error: ApolloError) => {
    const { graphQLErrors = [] } = error;

    return graphQLErrors.find((err) => isGeoGraphqlError(err));
};

const SearchPageFactory = (props: SearchPageFactoryProps) => {
    const SearchPage: NextPage<SearchPagePropsWithApollo> = (ssrPageProps) => {
        const clientRoutingLoading = useClientSideRoutingLoading();
        const [loadingData, setLoadingData] = useState(CLIENT_SIDE_DATA_FETCH);
        const router = useRouter();
        const [pageProps, setPageProps] =
            useState<SearchPagePropsWithApollo | null>(
                CLIENT_SIDE_DATA_FETCH ? null : ssrPageProps
            );

        useEffect(() => {
            async function fetchPropsOnClient() {
                setLoadingData(true);
                const ctx = {
                    pathname: router.pathname,
                    query: router.query,
                    asPath: router.asPath,
                    /* todo: replace with fetch */
                    apolloClient: ssrPageProps.apollo,
                    store: {},
                    AppTree: () => null
                };
                const gipResult = await GIP()?.(ctx);
                setPageProps(gipResult.initialProps);
                setLoadingData(false);
            }
            if (router.isReady && CLIENT_SIDE_DATA_FETCH) fetchPropsOnClient();
        }, [
            router.asPath,
            router.isReady,
            router.pathname,
            router.query,
            ssrPageProps.apollo
        ]);

        useEffect(() => {
            if (!CLIENT_SIDE_DATA_FETCH) setPageProps(ssrPageProps);
        }, [ssrPageProps]);

        const {
            analyticsTaxonomy,
            initialState,
            regularListingsFormatted,
            extendedListingsFormatted,
            featuredProperties,
            canonicalPath,
            filtersTargeting,
            transactionType,
            searchResults,
            breadcrumbs,
            discoverMore,
            links,
            error,
            useNewQuery
        } = pageProps ?? {};

        useEffect(() => {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'optimizely_feature',
                eventAction: 'show_listings_halo',
                eventLabel: useNewQuery ? 'variation_1' : 'original'
            });
        }, [useNewQuery]);

        const { query } = router;

        const { dfpAdTargeting, seoAccordions } = initialState ?? {};

        const currentPage =
            typeof query['pn'] === 'string' ? parseInt(query['pn']) : 1;

        const numberOfRegularListings =
            regularListingsFormatted && regularListingsFormatted.length
                ? regularListingsFormatted.length
                : 0;

        return (
            <InformationLayout
                main={
                    <SearchProvider initialState={initialState}>
                        <SearchResultsLayout
                            dfpAdTargeting={{
                                ...dfpAdTargeting,
                                ...filtersTargeting
                            }}
                            seoAccordions={seoAccordions ?? []}
                            numberOfRegularListings={numberOfRegularListings}
                            pageNumber={currentPage}
                            links={links as Links}
                            analyticsTaxonomy={analyticsTaxonomy}
                        >
                            <SearchResults
                                regularListings={regularListingsFormatted ?? []}
                                extendedListings={
                                    extendedListingsFormatted ?? []
                                }
                                featuredProperties={featuredProperties ?? []}
                                canonicalPath={canonicalPath ?? ''}
                                searchResults={searchResults}
                                breadcrumbs={breadcrumbs}
                                discoverMore={discoverMore}
                                transactionType={transactionType ?? 'sale'}
                                loading={clientRoutingLoading || loadingData}
                                error={error}
                                links={links as Links}
                            />
                        </SearchResultsLayout>
                    </SearchProvider>
                }
            />
        );
    };

    function GIP() {
        return wrapper.getInitialPageProps((store) =>
            withSSROptimizely.getInitialProps(
                async (ctx: NextPageContextApp) => {
                    const { res, asPath, apolloClient, query, req } = ctx;
                    const path = removeEverythingAfterHash(asPath);
                    const searchType = props.type;
                    const initialSearchState = generateInitialSearchState({
                        searchType
                    });

                    const isServer = !!req;

                    if (typeof window === 'undefined') {
                        // eslint-disable-next-line @typescript-eslint/no-var-requires
                        const newrelic = require('newrelic');
                        newrelic.setTransactionName(`/${searchType}/`);
                    }

                    if (isServer) {
                        res?.setHeader('Link', [
                            '<https://lid.zoocdn.com>; rel="preconnect"',
                            '<https://st.zoocdn.com/>; rel="preconnect"',
                            '<https://cdn.prod.zoopla.co.uk/>; rel="preconnect"',
                            '<https://r.zoocdn.com/>; rel="preconnect"',
                            '<https://api.segment.io>; rel="preconnect"',
                            '<https://scripts.prod.zoopla.co.uk>; rel="preconnect"',
                            '<https://securepubads.g.doubleclick.net>; rel="preconnect"',
                            '<https://www.googletagmanager.com>; rel="preconnect"',
                            '<https://gdpr.privacymanager.io>; rel="preconnect"',
                            '<https://gdpr-wrapper.privacymanager.io>; rel="preconnect"'
                        ]);
                    }

                    try {
                        let userAgent;
                        if (isServer) {
                            userAgent = req?.headers['user-agent'];
                        } else {
                            userAgent = navigator.userAgent;
                        }

                        const isMobile = Boolean(
                            userAgent?.match(
                                /\bAndroid\b.*\bMobile\b|BlackBerry|iPhone|iPod|Opera Mini|IEMobile|WPDesktop/i
                            )
                        );

                        const listingsQuery = isMobile
                            ? LISTINGS_QUERY_V2
                            : LISTINGS_QUERY;
                        const { data } =
                            (await apolloClient?.query({
                                query: listingsQuery,
                                variables: { path }
                            })) || {};

                        const { breadcrumbs, discoverMore, redirects } = data;

                        if (isServer) {
                            const shouldRedirect =
                                redirects && redirects.length > 0;
                            if (shouldRedirect) {
                                handleRedirects(redirects, res);

                                return {
                                    initialState: initialSearchState
                                };
                            }
                        }

                        let searchResults;
                        let serverRenderedData;

                        if (isMobile) {
                            searchResults = data.searchResultsV2;
                            serverRenderedData = getServerRenderedDataV2(
                                data,
                                initialSearchState
                            );
                        } else {
                            searchResults = data.searchResults;
                            serverRenderedData = getServerRenderedData(
                                data,
                                initialSearchState
                            );
                        }

                        const {
                            regularListingsFormatted,
                            extendedListingsFormatted,
                            featuredProperties,
                            searchContextData,
                            canonicalPath,
                            analyticsTaxonomy,
                            transactionType,
                            filtersTargeting,
                            links,
                            totalResults,
                            pageNumber,
                            pageNumberMax,
                            totalResultsWasLimited,
                            polyenc,
                            filtersFields
                        } = serverRenderedData;

                        const sortOrder = { ...serverRenderedData.sortOrder };

                        if (sortOrder.options) {
                            sortOrder.options = sortOrder.options.filter(
                                (option) => {
                                    return option.value !== 'most_popular';
                                }
                            );
                        }

                        dispatchData(
                            store,
                            {
                                sortOrder,
                                totalResults,
                                pageNumber,
                                pageNumberMax,
                                totalResultsWasLimited,
                                polyenc,
                                filtersFields
                            },
                            query
                        );

                        const props = {
                            analyticsTaxonomy: analyticsTaxonomy,
                            initialState:
                                searchContextData || initialSearchState,
                            regularListingsFormatted: regularListingsFormatted,
                            extendedListingsFormatted:
                                extendedListingsFormatted,
                            featuredProperties,
                            canonicalPath: canonicalPath,
                            transactionType,
                            searchResults,
                            breadcrumbs,
                            discoverMore,
                            filtersTargeting,
                            links,
                            useNewQuery: isMobile
                        };

                        // we don't use searchResults.listings in the page
                        const { listings, ...searchResultMeta } = searchResults;

                        const compressedProps = {
                            ...props,
                            regularListingsFormatted: compressListings(
                                regularListingsFormatted
                            ),
                            extendedListingsFormatted: compressListings(
                                extendedListingsFormatted
                            ),
                            featuredProperties: featuredProperties.map((fp) => {
                                if (typeof fp === 'string') return fp;
                                // we don't show Featured Property galleries
                                const { otherPropertyImages, ...rest } = fp;
                                return rest;
                            }),
                            searchResults: searchResultMeta
                        };
                        return compressedProps;
                    } catch (error) {
                        const geoError = getGeoError(error as ApolloError);
                        if (geoError) {
                            const errLocation: string =
                                geoError?.extensions?.['response']?.body
                                    ?.errors[0]?.validation_errors[0]
                                    ?.current_value;

                            const section = asPath?.includes('/for-sale')
                                ? 'for-sale'
                                : 'to-rent';

                            if (isServer) {
                                const redirect: Redirect = {
                                    redirectUri: `/search/?section=${section}&q=${
                                        errLocation || ''
                                    }`,
                                    code: 302
                                };
                                handleRedirects([redirect], res);
                            } else {
                                await Router.push({
                                    pathname: '/search/',
                                    query: { section, q: errLocation }
                                });
                            }
                        }

                        return {
                            initialState: initialSearchState,
                            error
                        };
                    }
                }
            )
        );
    }

    if (!CLIENT_SIDE_DATA_FETCH) {
        SearchPage.getInitialProps = GIP();
    }

    return {
        // SearchPage exported here just for testing
        SearchPage,
        SearchPageWithApollo: wrapper.withRedux(graphQLWrapper(SearchPage))
    };
};

/**
 * With data for hundreds of images on a single LSRP page the JSON markup quickly
 * adds up to a very heavy page. Replace the listing galleries image list with
 * a slimmed down tuple array
 */
function compressListings<
    T extends {
        otherPropertyImages?: Listing['otherPropertyImages'];
    }[] = Record<string, unknown>[]
>(listings: T) {
    return listings.map((l) => {
        const { otherPropertyImages, ...rest } = l;

        const gallery = otherPropertyImages?.reduce<Gallery>((acc, curr) => {
            if (curr && curr.large) {
                const src = curr.large.startsWith(
                    'https://lid.zoocdn.com/645/430/'
                )
                    ? curr.large.replace('https://lid.zoocdn.com/645/430', '')
                    : curr.large;
                return [
                    ...acc,
                    [curr.caption ?? null, src] as [string | null, string]
                ];
            }
            return acc;
        }, []);
        return {
            ...rest,
            gallery
        };
    });
}

export default SearchPageFactory;
